import React, { useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as PendingIcon,
  Timeline as TimelineIcon,
  Event as EventIcon,
  ArrowForward as ArrowIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { motion, useAnimation, useInView } from 'framer-motion';
import { format } from 'date-fns';

interface TimelineEvent {
  id: string;
  date: Date;
  title: string;
  description?: string;
  type: 'assessment' | 'milestone' | 'improvement' | 'report';
  status: 'completed' | 'pending' | 'upcoming';
  category?: string;
  value?: number;
  unit?: string;
  icon?: React.ReactNode;
  color?: string;
  topChanges?: Array<{
    name: string;
    change: number;
    isImprovement: boolean;
    value: number;
    unit: string;
  }>;
  metrics?: any[];
  significance?: number;
}

interface AssessmentTimelineProps {
  events: TimelineEvent[];
  onEventClick?: (event: TimelineEvent) => void;
  variant?: 'vertical' | 'horizontal';
  showConnector?: boolean;
  delay?: number;
}

export const AssessmentTimeline: React.FC<AssessmentTimelineProps> = ({
  events,
  onEventClick,
  variant = 'vertical',
  showConnector = true,
  delay = 0,
}) => {
  const theme = useTheme();
  const controls = useAnimation();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const getEventIcon = (event: TimelineEvent) => {
    if (event.icon) return event.icon;
    
    switch (event.type) {
      case 'assessment':
        return event.status === 'completed' ? <CheckIcon /> : <PendingIcon />;
      case 'milestone':
        return <StarIcon />;
      case 'improvement':
        return <EventIcon />;
      case 'report':
        return <TimelineIcon />;
      default:
        return <EventIcon />;
    }
  };

  const getEventColor = (event: TimelineEvent) => {
    if (event.color) return event.color;
    
    switch (event.status) {
      case 'completed':
        return theme.palette.success.main;
      case 'pending':
        return theme.palette.warning.main;
      case 'upcoming':
        return theme.palette.info.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        delay: Math.min(delay, 0.1),
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: variant === 'vertical' ? -20 : 0, y: variant === 'horizontal' ? 20 : 0 },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const connectorVariants = {
    hidden: { scaleY: 0, originY: 0 },
    visible: {
      scaleY: 1,
      transition: {
        duration: 0.4,
      },
    },
  };

  const renderVerticalTimeline = () => (
    <Box sx={{ position: 'relative', pl: 4 }}>
      {events.map((event, index) => (
        <motion.div
          key={event.id}
          variants={itemVariants}
          style={{ position: 'relative' }}
        >
          {/* Connector line */}
          {showConnector && index < events.length - 1 && (
            <motion.div
              variants={connectorVariants}
              style={{
                position: 'absolute',
                left: -28,
                top: 44,
                width: 2,
                height: 100,
                backgroundColor: alpha(theme.palette.divider, 0.3),
                transformOrigin: 'top',
              }}
            />
          )}

          {/* Timeline dot */}
          <motion.div
            whileHover={{ scale: 1.2 }}
            style={{
              position: 'absolute',
              left: -40,
              top: 16,
              width: 24,
              height: 24,
              borderRadius: '50%',
              backgroundColor: getEventColor(event),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 0 4px ${alpha(getEventColor(event), 0.2)}`,
              cursor: onEventClick ? 'pointer' : 'default',
            }}
            onClick={() => onEventClick?.(event)}
          >
            <Box sx={{ color: 'white', fontSize: 16 }}>
              {getEventIcon(event)}
            </Box>
          </motion.div>

          {/* Event card */}
          <Paper
            elevation={event.type === 'milestone' ? 2 : 1}
            sx={{
              mb: 3,
              p: 2.5,
              borderRadius: 2,
              cursor: onEventClick ? 'pointer' : 'default',
              transition: 'all 0.2s ease-out',
              border: event.type === 'milestone'
                ? `2px solid ${alpha(theme.palette.warning.main, 0.3)}`
                : `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              backgroundColor: event.type === 'milestone'
                ? alpha(theme.palette.warning.main, 0.05)
                : theme.palette.background.paper,
              '&:hover': onEventClick ? {
                transform: 'translateY(-2px)',
                boxShadow: 3,
                borderColor: getEventColor(event),
              } : {},
            }}
            onClick={() => onEventClick?.(event)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {event.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {format(event.date, 'MMMM d, yyyy')}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {event.category && (
                  <Chip
                    label={event.category}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      backgroundColor: alpha(getEventColor(event), 0.1),
                      color: getEventColor(event),
                    }}
                  />
                )}
                <Chip
                  label={event.status}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    borderColor: getEventColor(event),
                    color: getEventColor(event),
                  }}
                />
              </Box>
            </Box>

            {event.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {event.description}
              </Typography>
            )}

            {event.value !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 1 }}>
                <Typography variant="h5" fontWeight={700} color={getEventColor(event)}>
                  {event.value}
                </Typography>
                {event.unit && (
                  <Typography variant="body2" color="text.secondary">
                    {event.unit}
                  </Typography>
                )}
              </Box>
            )}

            {/* Show top changes if available */}
            {event.topChanges && event.topChanges.length > 0 && (
              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                  Top Changes:
                </Typography>
                {event.topChanges.map((change: any, idx: number) => (
                  <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {change.name}
                    </Typography>
                    <Chip
                      label={`${change.change > 0 ? '+' : ''}${change.change.toFixed(1)}%`}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.7rem',
                        backgroundColor: change.isImprovement ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                        color: change.isImprovement ? theme.palette.success.main : theme.palette.error.main,
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </motion.div>
      ))}
    </Box>
  );

  const renderHorizontalTimeline = () => (
    <Box sx={{ position: 'relative', overflowX: 'auto', pb: 2 }}>
      <Box sx={{ display: 'flex', minWidth: 'max-content', position: 'relative', pt: 8 }}>
        {/* Connector line */}
        {showConnector && (
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1, delay: delay + 0.3 }}
            style={{
              position: 'absolute',
              top: 28,
              left: 40,
              right: 40,
              height: 2,
              backgroundColor: alpha(theme.palette.divider, 0.3),
              transformOrigin: 'left',
            }}
          />
        )}

        {events.map((event, index) => (
          <motion.div
            key={event.id}
            variants={itemVariants}
            style={{
              position: 'relative',
              minWidth: 250,
              marginRight: index < events.length - 1 ? 32 : 0,
            }}
          >
            {/* Timeline dot */}
            <motion.div
              whileHover={{ scale: 1.2 }}
              style={{
                position: 'absolute',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: getEventColor(event),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 0 0 4px ${alpha(getEventColor(event), 0.2)}`,
                cursor: onEventClick ? 'pointer' : 'default',
                zIndex: 1,
              }}
              onClick={() => onEventClick?.(event)}
            >
              <Box sx={{ color: 'white', fontSize: 16 }}>
                {getEventIcon(event)}
              </Box>
            </motion.div>

            {/* Event card */}
            <Paper
              elevation={event.type === 'milestone' ? 2 : 1}
              sx={{
                mt: 6,
                p: 2,
                borderRadius: 2,
                cursor: onEventClick ? 'pointer' : 'default',
                transition: 'all 0.2s ease-out',
                border: event.type === 'milestone'
                  ? `2px solid ${alpha(theme.palette.warning.main, 0.3)}`
                  : `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                backgroundColor: event.type === 'milestone'
                  ? alpha(theme.palette.warning.main, 0.05)
                  : theme.palette.background.paper,
                '&:hover': onEventClick ? {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                  borderColor: getEventColor(event),
                } : {},
              }}
              onClick={() => onEventClick?.(event)}
            >
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                {event.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                {format(event.date, 'MMM d, yyyy')}
              </Typography>
              
              {event.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {event.description}
                </Typography>
              )}

              {event.value !== undefined && (
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 1 }}>
                  <Typography variant="h6" fontWeight={700} color={getEventColor(event)}>
                    {event.value}
                  </Typography>
                  {event.unit && (
                    <Typography variant="body2" color="text.secondary">
                      {event.unit}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Show top changes if available */}
              {event.topChanges && event.topChanges.length > 0 && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                    Top Changes:
                  </Typography>
                  {event.topChanges.map((change: any, idx: number) => (
                    <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }} noWrap>
                        {change.name}
                      </Typography>
                      <Chip
                        label={`${change.change > 0 ? '+' : ''}${change.change.toFixed(1)}%`}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          ml: 1,
                          backgroundColor: change.isImprovement ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                          color: change.isImprovement ? theme.palette.success.main : theme.palette.error.main,
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </motion.div>
        ))}
      </Box>
    </Box>
  );

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={containerVariants}
    >
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimelineIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Progress Timeline
            </Typography>
            {events.length > 0 && (
              <Chip
                label={`${events.length} events`}
                size="small"
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </Box>

        {/* Timeline */}
        {events.length > 0 ? (
          variant === 'vertical' ? renderVerticalTimeline() : renderHorizontalTimeline()
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <TimelineIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No assessment timeline available yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete assessments to see progress over time
            </Typography>
          </Box>
        )}
      </Paper>
    </motion.div>
  );
};

export default AssessmentTimeline;